<template>
  <KidHanziCardLayout :hide-role-image="face.style.hideRoleImage">
    <template #content>
      <DebutCardBadge
        v-if="face.isFirstLearn"
        class="absolute top-0 left-0"
      />

      <WrongCardBadge
        v-if="face.isWrongCard"
        class="absolute top-0 left-0"
      />

      <div class="w-full h-full flex flex-col justify-center items-center">
        <!-- 只显示解析（例句），不显示 currentHanzi -->
        <div
          class="text-xl text-center max-w-md leading-relaxed question-text-large"
        >
          <template
            v-for="(segment, index) in displaySegments"
            :key="index"
          >
            <span v-if="segment.type === 'text'">{{ segment.text }}</span>
            <span
              v-else-if="segment.type === 'cloze'"
              class="g-cloze cursor-pointer cloze"
              :class="{
                focused: data.focusOn === segment.index,
                filled:
                  segment.index !== undefined &&
                  data.inputMap[segment.index]?.text,
                transparent:
                  segment.index !== undefined &&
                  !data.inputMap[segment.index]?.text &&
                  !data.passed,
              }"
              @click="onClozeClick(segment as any)"
            >
              {{
                segment.index !== undefined
                  ? data.inputMap[segment.index]?.text ||
                    data.clozes[segment.index]?.text
                  : ''
              }}
            </span>
          </template>
        </div>
      </div>
    </template>

    <template #options>
      <div :class="operationLayoutClass">
        <Button
          v-for="(op, i) in data.options"
          :key="i"
          :scene="getOptionScene(op)"
          :class="[...getOptionClass(op), 'option-large']"
          @click="onClickOption(op)"
        >
          <div
            v-if="isLargeScreen"
            class="keyboard-tip"
            :class="op.used ? '' : 'text-gray-400'"
          >
            {{ i + 1 }}
          </div>
          <SparkleText
            :tag="op.used"
            class="sparkle-text"
          >
            <AutoFontSizeByText
              v-if="operationLayoutClass === 'operation-grid'"
              :font-size-list="[48, 40, 32, 28]"
            >
              {{ op.text }}
            </AutoFontSizeByText>
            <AutoFontSizeByLine
              v-else
              class="text-start"
              :font-size-list="[48, 40, 32, 28]"
            >
              {{ op.text }}
            </AutoFontSizeByLine>
          </SparkleText>
        </Button>
      </div>
    </template>
  </KidHanziCardLayout>
</template>

<script lang="ts" setup>
import { KidHanziCardFaceType, QuizStage, UnitEventType } from '@/types/core'
import { reactive, inject, computed, watch } from 'vue'
import { t } from '@/i18n'
import { useHotKey } from '@/hooks'
import { getOperationLayoutClass } from '@/components/ConcreteCard/layout'
import KidHanziCardLayout from '@/components/ConcreteCard/layout/KidHanziCardLayout.vue'
import { FeedbackStar, showCardDetail } from '@/components/ConcreteCard/common'
import DebutCardBadge from '@/components/ConcreteCard/common/DebutCardBadge.vue'
import WrongCardBadge from '@/components/ConcreteCard/common/WrongCardBadge.vue'
import { randomPick, wait } from '@/utils/misc'
import {
  processKidHanziFace,
  stringifyKidHanziContent,
  extractHanziClozePositions,
  generateHanziClozeOptions,
} from '@/utils/card'
import Button from '@/components/Button.vue'

import '@/components/ConcreteCard/layout/style.css'

import type { ComputedRef } from 'vue'
import type { ClozeChoiceKidHanziFace } from '@/types/core'
import SparkleText from '@/components/SparkleText.vue'
import AutoFontSizeByText from '@/components/AutoFontSizeByText.vue'
import AutoFontSizeByLine from '@/components/AutoFontSizeByLine.vue'

const props = defineProps<{
  face: ClozeChoiceKidHanziFace
  quizStage?: QuizStage
}>()

const isLargeScreen = inject<ComputedRef<boolean>>('isLargeScreen')
const onEvent = inject<(event: UnitEventType) => void>('onEvent')
const onNext = inject<VoidFunction>('onNext')
const onStar = inject<(star: FeedbackStar) => void>('onStar')

// quiz
const changeAnswer = inject<(answered: boolean) => void>('answerChange')
const resultChange = inject<(correct: boolean) => void>('resultChange')

useHotKey('tab,down,right', () => {
  if (data.passed) return
  nextCloze()
})

useHotKey('up,left', () => {
  if (data.passed) return
  prevCloze()
})

useHotKey('del,backspace', () => {
  if (data.passed) return
  if (data.focusOn != null) {
    const cloze = data.clozes[data.focusOn!]
    onClozeClick({ type: 'cloze', text: cloze.text, index: cloze.index })
  }
})

useHotKey('1,2,3,4,5,6,7,8,9,0', (evt: KeyboardEvent) => {
  evt.preventDefault()
  const key = evt.key
  const index = (key === '0' ? 10 : Number(key)) - 1

  if (index >= data.options.length) return

  const op = data.options[index]
  onClickOption(op)
})

const finalFace = computed(() => processKidHanziFace(props.face))

const currentHanzi = computed(() => finalFace.value.card.hanzi[0]?.[1] || '')

const data = reactive({
  star: FeedbackStar.One,
  passed: false,
  animationClass: '',

  clozes: [] as Array<{
    index: number
    start: number
    end: number
    text: string
  }>,
  options: [] as Array<{
    text: string
    isAnswer: boolean
    used: boolean
    wrong: boolean
  }>,
  // 输入的答案
  inputMap: {} as Record<number, { text: string; isAnswer: boolean } | null>,
  // 答案的FeedbackStar
  resultMap: {} as Record<number, 'replace' | 'remove' | 'wrong' | null>,

  tipTimes: 0,
  showTip: false,
  focusOn: 0 as number | null,
})

// 初始化挖空和选项
const initClozeData = () => {
  const analysis = finalFace.value.card.analysis
  const positions = extractHanziClozePositions(analysis, currentHanzi.value)

  if (positions.length === 0) {
    // 如果没有找到挖空位置，fallback到VoiceChoice
    return false
  }

  // 随机选择一个挖空位置
  const selectedPosition = randomPick(positions)
  data.clozes = [
    {
      index: 0,
      start: selectedPosition.start,
      end: selectedPosition.end,
      text: selectedPosition.text,
    },
  ]

  // 生成选项
  data.options = generateHanziClozeOptions(
    finalFace.value.card,
    finalFace.value.altCards,
    selectedPosition.text
  )

  return true
}

// 初始化数据
if (!initClozeData()) {
  // 如果初始化失败，应该fallback到VoiceChoice
  // 这里我们直接抛出错误，让上层处理
  throw new Error('Failed to initialize cloze data')
}

const clozeCount = computed(() => data.clozes.length)

const displaySegments = computed(() => {
  const analysis = finalFace.value.card.analysis
  const text = stringifyKidHanziContent(analysis)
  const segments: Array<{
    type: 'text' | 'cloze'
    text: string
    index?: number
  }> = []

  let lastEnd = 0

  for (const cloze of data.clozes) {
    // 添加挖空前的内容
    if (cloze.start > lastEnd) {
      segments.push({
        type: 'text',
        text: text.slice(lastEnd, cloze.start),
      })
    }

    // 添加挖空
    segments.push({
      type: 'cloze',
      text: data.inputMap[cloze.index]?.text || '',
      index: cloze.index,
    })

    lastEnd = cloze.end
  }

  // 添加剩余内容
  if (lastEnd < text.length) {
    segments.push({
      type: 'text',
      text: text.slice(lastEnd),
    })
  }

  return segments
})

const operationLayoutClass = computed(() => {
  // 2x2 grid 布局
  return 'operation-grid'
})

function getOptionClass(op: {
  text: string
  isAnswer: boolean
  used?: boolean
  wrong?: boolean
}): string[] {
  const classList = ['option']
  if (operationLayoutClass.value === 'operation-grid') {
    if (data.options.length === 2) {
      classList.push('aspect-square')
    } else {
      classList.push('aspect-[3/2]')
    }
  }

  if (op.wrong) {
    classList.push('wrong')
  }

  // 由于现在只有选对时选项才会被使用，所以这里直接展示答对的样式
  if (op.used) {
    classList.push('correct')
  }

  // 当答完时，未使用的答案变灰即可
  if (data.passed && !op.used) {
    classList.push('used')
  }

  return classList
}

function getOptionScene(op: {
  text: string
  isAnswer: boolean
  used?: boolean
  wrong?: boolean
}):
  | 'choice'
  | 'choiceSelected'
  | 'choiceCorrect'
  | 'choiceWrong'
  | 'choiceUsed' {
  let classList = getOptionClass(op)

  if (classList.includes('used')) {
    return 'choiceUsed'
  }
  if (classList.includes('correct')) {
    return 'choiceCorrect'
  }
  if (classList.includes('wrong')) {
    return 'choiceWrong'
  }

  return 'choice'
}

function onCheckResult(): boolean {
  const correct = data.clozes.every(cloze => {
    const input = data.inputMap[cloze.index]
    return input != null && input.isAnswer
  })

  resultChange?.(correct)
  return correct
}

function animeOptionWrong(op: {
  text: string
  isAnswer: boolean
  used?: boolean
  wrong?: boolean
}) {
  op.wrong = true
  setTimeout(() => {
    op.wrong = undefined
  }, 400)
}

// 自动切换到下一张，此时需要等待一些事件的完成，比如这里是等动画结束
async function onAutoNext() {
  onStar?.(data.star)

  // TODO(buding): 如何准确获取动画结束，这里先 hardcode
  await wait(700)

  // 如果是新知识且设置的展示卡片详情，此时会先弹出卡片详情
  if (props.face.isFirstLearn && props.face.style.showCardDetail) {
    showCardDetail(props.face.card, props.face.isFirstLearn, onNext)
    return
  }

  onNext?.()
}

function onClickOption(op: {
  text: string
  isAnswer: boolean
  used?: boolean
  wrong?: boolean
}) {
  if (op.used) return
  if (data.passed) return

  if (data.focusOn == null) {
    _message.info(t('cardview.focus_first'))
    return
  }

  const inputMap = {
    ...data.inputMap,
    [data.focusOn]: op,
  }

  // 全部做对，卡片结束，自动跳转下一张
  if (op.isAnswer) {
    op.used = true
    data.inputMap[data.focusOn] = op
    data.star = data.tipTimes > 0 ? FeedbackStar.Two : FeedbackStar.Three
    data.passed = true
    onEvent?.(UnitEventType.CORRECT)
    onAutoNext()
  } else {
    // 当前选择的选项不正确
    data.tipTimes++
    animeOptionWrong(op)
    onEvent?.(UnitEventType.WRONG)
  }
}

function onClozeClick(cloze: {
  type: 'text' | 'cloze'
  text: string
  index?: number
}) {
  if (data.passed) return
  if (cloze.index == null) return

  data.focusOn = cloze.index

  const input = data.inputMap[cloze.index]

  if (input) {
    // 如果已经有答案，清除它
    const op = data.options.find(o => o.text === input.text)
    if (op) {
      op.used = false
    }
    data.inputMap[cloze.index] = null
    onCheckResult()
  }
}

function nextEmptyCloze() {
  const allClozesFilled = data.clozes.every(cloze => {
    const input = data.inputMap[cloze.index]
    return input != null
  })

  // 如果全部填写过了，取消挖空即可
  if (allClozesFilled) {
    data.focusOn = null
    return
  }

  nextCloze()

  // 直到聚焦的挖空没有填写过时停止
  while (data.inputMap[data.focusOn!] != null) {
    nextCloze()
  }
}

function nextCloze() {
  if (data.focusOn === data.clozes.length - 1) {
    data.focusOn = 0
  } else if (data.focusOn != null) {
    data.focusOn++
  } else {
    data.focusOn = 0
  }
}

function prevCloze() {
  if (data.focusOn === 0) {
    data.focusOn = data.clozes.length - 1
  } else if (data.focusOn != null) {
    data.focusOn--
  }
}

watch(data.inputMap, () => {
  const selectedOptionsCount = data.options.filter(op => op.used).length
  changeAnswer?.(selectedOptionsCount !== 0)
})
</script>

<style scoped>
.button {
  font-size: 14px;
}

.passed {
  animation: correct 0.2s;
}

.unpassed {
  animation: wrong 0.2s;
}

.option {
  cursor: pointer;
  position: relative;
  justify-content: center;
}

.used {
  color: transparent;
}

.keyboard-tip {
  margin-right: 6px;
  border-width: 1px;
  border-radius: 4px;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  border: 1px solid var(--gray-400);
  color: var(--gray-400);
}
/* Grid 显示快捷键时，快捷键是浮在左上角的，不影响文字的布局 */
.operation-grid .keyboard-tip {
  position: absolute;
  top: 6px;
  left: 6px;
}

.option-large {
  min-height: 80px;
  font-size: 2rem;
  /* padding: 16px 0;  // 移除上下内边距 */
  padding: 0;
}
.cloze {
  /* 默认状态不设置颜色 */
}

.cloze.transparent {
  color: transparent;
}

.cloze.filled {
  color: var(--ld-brand-500);
}
.focused {
  border-bottom: 2px solid var(--ld-brand-500);
}
.question-text-large {
  font-size: 2em;
}
</style>
